import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, TrendingUp, Target } from "lucide-react";

/**
 * Pre-market screener — NIFTY 50 shortlist scored on 6 intraday-friendly
 * criteria. The worker cron refreshes every 30s from 09:00-09:20 IST and
 * every 5 min through the session; users can also force-refresh here.
 *
 * NOTE: this page currently displays synthetic-but-deterministic screener
 * output. Live Kite integration (real per-symbol OHLC + OI) is Phase 2.
 * A banner clearly labels the source.
 */
const COLUMNS = [
  { key: "symbol", label: "Symbol", num: false },
  { key: "sector", label: "Sector", num: false },
  { key: "ltp", label: "LTP", num: true, fmt: (v) => v?.toFixed(2) },
  { key: "gap_pct", label: "Gap %", num: true, fmt: (v) => v?.toFixed(2) + "%", tone: true },
  { key: "rel_volume", label: "RelVol", num: true, fmt: (v) => v?.toFixed(2) + "×" },
  { key: "atr14_pct", label: "ATR14%", num: true, fmt: (v) => v?.toFixed(2) + "%" },
  { key: "rs_vs_nifty", label: "RS vs NIFTY", num: true, fmt: (v) => v?.toFixed(2) + "%", tone: true },
  { key: "options_oi_lots", label: "Options OI", num: true, fmt: (v) => v?.toLocaleString("en-IN") },
  { key: "sector_momentum", label: "Sector Mom", num: true, fmt: (v) => v?.toFixed(2) + "%", tone: true },
  { key: "composite_score", label: "Score", num: true, fmt: (v) => v?.toFixed(3), tone: true },
];

export default function Screener() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sortKey, setSortKey] = useState("composite_score");
  const [sortDir, setSortDir] = useState("desc");
  const [view, setView] = useState("top10");  // top10 | full

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/screener/shortlist");
      setData(data);
    } catch (e) {
      toast.error("Failed to load screener");
    }
  }, []);

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/screener/refresh");
      toast.success(`Screener refreshed — ${data.count} names ranked`);
      await load();
    } catch (e) {
      toast.error("Refresh failed");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const rows = useMemo(() => {
    const source = view === "top10" ? (data?.top_10 || []) : (data?.full_ranking || []);
    const sorted = [...source].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0);
    });
    return sorted;
  }, [data, view, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  };

  return (
    <div className="space-y-5" data-testid="screener-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <Target size={20} className="text-blue-400" />
            <h1 className="text-2xl tracking-tight text-slate-100">
              Pre-Market Screener
            </h1>
          </div>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl uppercase tracking-[0.15em]">
            NIFTY 50 · scored on gap, volume, volatility, relative strength,
            options liquidity &amp; sector momentum. Refreshes every 30s during
            09:00-09:20 IST warm-up, then every 5 min through the session.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
            {data?.computed_at ? (
              <>Updated <span className="text-slate-300 font-mono">
                {new Date(data.computed_at).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata" })}
              </span></>
            ) : "No data yet"}
            {data?.is_stale && (
              <span className="ml-2 text-amber-400">(previous session)</span>
            )}
          </div>
          <button
            data-testid="screener-refresh-btn"
            onClick={refresh}
            disabled={busy}
            className="px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-[0.15em] border border-blue-500 text-blue-300 hover:bg-blue-500/10 flex items-center gap-1.5 disabled:opacity-50"
          >
            <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Data source banner */}
      <div className="rounded-sm border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-200"
           data-testid="screener-source-banner">
        <strong>Phase 1 (this build):</strong> screener output is synthetic-but-deterministic —
        it exercises the entire pipeline (composite scoring, cron, UI, sort/filter) so you can
        validate the workflow. <strong>Phase 2</strong> wires Kite historical_data + real
        options OI for live NIFTY 50 quotes.
      </div>

      {/* View toggle + top 3 callouts */}
      <div className="flex items-center gap-2">
        {["top10", "full"].map((k) => (
          <button
            key={k}
            data-testid={`screener-view-${k}`}
            onClick={() => setView(k)}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] rounded-sm border transition-colors ${
              view === k
                ? "bg-blue-600 border-blue-500 text-white"
                : "border-slate-700 text-slate-400 hover:border-slate-600"
            }`}
          >
            {k === "top10" ? "Top 10" : "Full 50"}
          </button>
        ))}
      </div>

      {/* Top 3 tiles */}
      {(data?.top_10?.length || 0) >= 3 && (
        <div className="grid md:grid-cols-3 gap-3">
          {data.top_10.slice(0, 3).map((r, i) => (
            <div key={r.symbol} className="surface rounded-sm p-4" data-testid={`screener-top-${i + 1}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">#{i + 1}</div>
                  <div className="text-lg font-mono text-slate-100 mt-1">{r.symbol}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{r.sector}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase text-slate-500">Score</div>
                  <div className="text-xl font-mono text-blue-300">{r.composite_score?.toFixed(2)}</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-y-1 gap-x-3 text-[11px] font-mono">
                <div className="text-slate-500">Gap</div>
                <div className={`text-right ${r.gap_pct > 0 ? "text-pos" : r.gap_pct < 0 ? "text-neg" : ""}`}>
                  {r.gap_pct?.toFixed(2)}%
                </div>
                <div className="text-slate-500">RelVol</div>
                <div className="text-right text-slate-200">{r.rel_volume?.toFixed(2)}×</div>
                <div className="text-slate-500">RS</div>
                <div className={`text-right ${r.rs_vs_nifty > 0 ? "text-pos" : "text-neg"}`}>
                  {r.rs_vs_nifty?.toFixed(2)}%
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Full sortable table */}
      <div className="surface rounded-sm overflow-hidden" data-testid="screener-table">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 bg-slate-900/40">
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => toggleSort(c.key)}
                    className={`${c.num ? "text-right" : "text-left"} px-3 py-2 cursor-pointer hover:text-slate-300`}
                    data-testid={`screener-col-${c.key}`}
                  >
                    {c.label}{sortKey === c.key ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center py-10 text-slate-500 text-xs">
                    No screener data yet. Click Refresh.
                  </td>
                </tr>
              )}
              {rows.map((r, i) => (
                <tr key={r.symbol} className="border-t border-slate-800/50 hover:bg-slate-900/40" data-testid="screener-row">
                  {COLUMNS.map((c) => {
                    const v = r[c.key];
                    const shown = c.fmt ? c.fmt(v) : v;
                    let cls = c.num ? "text-right font-mono" : "text-slate-200";
                    if (c.tone && typeof v === "number") {
                      cls += v > 0 ? " text-pos" : v < 0 ? " text-neg" : "";
                    }
                    return (
                      <td key={c.key} className={`px-3 py-1.5 ${cls}`}>{shown ?? "—"}</td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
